<template>
    <div
        :id="'product_' + productId"
        class="gh-product-card"
        @click="onClick"
        @mouseenter="triggerSlideshowStart(productId)"
        @mouseleave="triggerSlideshowStop(productId)"
    >
        <div
            class="gh-product-card__image-wrapper"
            :style="{
                cursor:
                    isPrinting || getProductExceptionItem(productType)
                        ? 'pointer'
                        : 'unset',
                border: isPrintItemsExists ? '2px solid #2c94ff' : 'none',
            }"
            @click="isPrinting ? togglePrintItem() : null"
        >
            <div class="gh-product-card__image">
                <slot name="product-image">
                    <image-slideshow
                        v-if="imageMode === IMAGE_MODE.SLIDESHOW"
                        :id="'product_image_' + productId"
                        :ref="'slideshowRef_' + productId"
                        :main-image="productMainImage"
                        :images="productImages"
                        :image-alt="productImageAlt"
                        :disabled="isPrinting"
                        :lazy-tag="lazyTag"
                        v-bind="imageCdnProps"
                    ></image-slideshow>
                    <image-swiper
                        v-else-if="imageMode === IMAGE_MODE.SWIPER"
                        :images="swiperImages"
                        :image-alt="productImageAlt"
                        :arrows="false"
                        :dots="true"
                        :allow-parent-scroll-on-swipe="true"
                        :lazy-tag="lazyTag"
                    ></image-swiper>
                </slot>

                <!-- overlay 與 image 大小一致，但其中的按鈕另做處理 -->
                <div
                    v-if="
                        showOverlay &&
                        !isPostProcessing &&
                        !isPrinting &&
                        layoutMode === LAYOUT_MODE.GRID
                    "
                    class="gh-product-card__overlay"
                    :style="
                        productImages.length > 0
                            ? { 'background-color': 'initial' }
                            : {}
                    "
                >
                    <slot name="grid-overlay"></slot>
                </div>

                <!-- 列印模式時不顯示 overlay，進行勾選處理 -->
                <div v-else-if="isPrinting" class="print-box">
                    <input
                        :id="'print_checkbox_' + productId"
                        type="checkbox"
                        :value="productId"
                        :checked="isPrintItemsExists"
                        @change="togglePrintItem"
                    />
                    <label
                        :for="'print_checkbox_' + productId"
                        class="print-checkbox-label"
                    ></label>
                </div>
            </div>

            <!-- 列表排列時，overlay 中的按鈕顯示於右側 -->
            <div
                v-if="
                    showOverlay &&
                    !isPostProcessing &&
                    !isPrinting &&
                    layoutMode === LAYOUT_MODE.LIST
                "
                :class="[
                    'gh-product-card__overlay',
                    'gh-product-card__overlay--' + layoutMode,
                ]"
            >
                <slot name="list-overlay"></slot>
            </div>
        </div>

        <div class="gh-product-card__info">
            <div class="gh-product-card__title">
                <slot name="product-title">
                    <div :title="productName">
                        <ellipsis-text
                            :text="productName"
                            :limited-line="2"
                            :offset-text="productCode"
                            class="gh-product-card__text"
                        >
                            <template #tail>
                                <span class="gh-product-card__code">
                                    {{ productCode }}
                                </span>
                            </template>
                        </ellipsis-text>
                    </div>
                </slot>
            </div>

            <div
                v-if="slots['product-subtitle'] || productSubtitle"
                class="gh-product-card__subtitle"
            >
                <slot name="product-subtitle">
                    <div :title="productSubtitle">
                        <ellipsis-text
                            :text="productSubtitle"
                            :limited-line="1"
                        ></ellipsis-text>
                    </div>
                </slot>
            </div>

            <div
                v-if="isPostProcessing || layoutMode === LAYOUT_MODE.LIST"
                :class="[
                    'gh-product-card__desc-detailed',
                    isPostProcessing ? 'is-post-processing-desc' : '',
                ]"
            >
                <div>{{ productDesc }}</div>
                <template v-if="layoutMode === LAYOUT_MODE.LIST">
                    <div v-if="!productProcessTime">
                        工作天數依廠內生產安排，如需確認工作天數，請洽業務及客服。
                    </div>
                    <div v-else>
                        工作天數：約 {{ productProcessTime }}
                        天，若廠內量大交期有可能會增加。
                    </div>
                </template>
            </div>

            <template v-if="!isPostProcessing">
                <div
                    v-if="slots['product-price']"
                    class="gh-product-card__price-area"
                >
                    <slot name="product-price"></slot>
                </div>
                <p v-else-if="productDesc" class="gh-product-card__desc">
                    {{ productDesc }}
                </p>
            </template>

            <slot name="product-btns"></slot>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, getCurrentInstance, nextTick, useSlots } from 'vue';
    import EllipsisText from '@/components/base/EllipsisText.vue';
    import ImageSlideshow from '@/components/base/ImageSlideshow.vue';
    import ImageSwiper from '@/components/base/ImageSwiper.vue';
    import { IMAGE_MODE } from '@/types/components/image-mode.types';
    import { LAYOUT_MODE } from '@/types/components/layout-mode.types';
    import { getProductExceptionItem } from '@/utils/general.utils';

    interface ImageSlideshowExposed {
        externalStartTrigger?: () => void;
        externalStopTrigger?: () => void;
    }

    interface ProductCardRefs {
        [key: string]:
            ImageSlideshowExposed | ImageSlideshowExposed[] | undefined;
    }

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            productId: string;
            productName: string;
            productCode: string;
            productType: number;
            productMainImage: string;
            productImages?: string[];
            productImageAlt?: string;
            productSubtitle?: string;
            productDesc?: string;
            productProcessTime?: string;
            layoutMode?: LAYOUT_MODE;
            imageMode?: IMAGE_MODE;
            cdnPrefixes?: string[];
            lazyTag?: string;
            showOverlay?: boolean;
            isPostProcessing?: boolean;
            isPrinting?: boolean;
            isPrintItemsExists?: boolean;
        }>(),
        {
            productImages: () => [],
            productImageAlt: '',
            productSubtitle: '',
            productDesc: '',
            productProcessTime: '',
            layoutMode: LAYOUT_MODE.DEFAULT,
            imageMode: IMAGE_MODE.SLIDESHOW,
            cdnPrefixes: () => [],
            lazyTag: '',
            showOverlay: false,
            isPostProcessing: false,
            isPrinting: false,
            isPrintItemsExists: false,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'click'): void;
        (event: 'toggle-print-item'): void;
    }>();

    // ----- attrs -----
    const slots = useSlots();

    // ----- state -----
    const vm = getCurrentInstance();

    // ----- computed -----
    const swiperImages = computed(() => {
        return [
            props.productMainImage,
            ...(Array.isArray(props.productImages) ? props.productImages : []),
        ];
    });

    // 只有明確提供 cdnPrefixes 時才傳入圖片元件
    const imageCdnProps = computed(() => {
        if (!Array.isArray(props.cdnPrefixes) || !props.cdnPrefixes.length) {
            return {};
        }

        return {
            cdnPrefixes: props.cdnPrefixes,
        };
    });

    // ----- methods -----
    const getSlideshowRef = (id: string): ImageSlideshowExposed | null => {
        const refs = vm?.proxy?.$refs as ProductCardRefs | undefined;
        const target = refs ? refs[`slideshowRef_${id}`] : null;

        if (Array.isArray(target)) {
            return target[0] || null;
        }

        return target || null;
    };

    // 點擊事件處理
    const onClick = (): void => {
        emits('click');
    };

    // 加入/移除列印項目，並且排列順序
    const togglePrintItem = (): void => {
        emits('toggle-print-item');
    };

    // 呼叫圖片幻燈片輪播
    const triggerSlideshowStart = (id: string): void => {
        if (props.imageMode !== IMAGE_MODE.SLIDESHOW) return;

        nextTick(() => {
            const slideshowRef = getSlideshowRef(id);
            if (
                slideshowRef &&
                typeof slideshowRef.externalStartTrigger === 'function'
            ) {
                slideshowRef.externalStartTrigger();
            }
        });
    };

    // 停止圖片幻燈片輪播
    const triggerSlideshowStop = (id: string): void => {
        if (props.imageMode !== IMAGE_MODE.SLIDESHOW) return;

        nextTick(() => {
            const slideshowRef = getSlideshowRef(id);
            if (
                slideshowRef &&
                typeof slideshowRef.externalStopTrigger === 'function'
            ) {
                slideshowRef.externalStopTrigger();
            }
        });
    };
</script>
